import { Head, Link } from '@inertiajs/react'
import SuperAdminLayout from '../_SuperAdminLayout'

interface PendingTenant {
  id: number
  proposed_org_name: string
  proposed_slug: string | null
  contact_email: string
  contact_full_name: string
  status: string
  requested_at_display: string
  reviewed_at_display: string | null
  provisioned_organization_id: number | null
}

interface Props { pending: PendingTenant[]; reviewed: PendingTenant[] }

export default function PendingTenantsIndex({ pending, reviewed }: Props) {
  return (
    <SuperAdminLayout>
      <Head title="Pending tenants" />
      <h1 className="text-2xl font-bold text-text-primary mb-6">Pending tenant signups</h1>

      <Section title={`Awaiting review (${pending.length})`}>
        {pending.length === 0 ? (
          <Empty text="Nothing waiting." />
        ) : (
          <Table rows={pending} reviewed={false} />
        )}
      </Section>

      <Section title={`Recently reviewed (${reviewed.length})`}>
        {reviewed.length === 0 ? (
          <Empty text="No history yet." />
        ) : (
          <Table rows={reviewed} reviewed={true} />
        )}
      </Section>
    </SuperAdminLayout>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-text-primary mb-3">{title}</h2>
      <div className="bg-white border border-stone/20 rounded-lg overflow-hidden">{children}</div>
    </div>
  )
}

function Empty({ text }: { text: string }) {
  return <div className="py-10 text-center text-text-muted text-sm">{text}</div>
}

function Table({ rows, reviewed }: { rows: PendingTenant[]; reviewed: boolean }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-[#F5F5F5] text-text-secondary">
        <tr>
          <th className="text-left py-3 px-4 font-semibold">Org</th>
          <th className="text-left py-3 px-4 font-semibold">Contact</th>
          <th className="text-left py-3 px-4 font-semibold">Email</th>
          <th className="text-left py-3 px-4 font-semibold">{reviewed ? 'Reviewed' : 'Requested'}</th>
          <th className="text-left py-3 px-4 font-semibold">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.id} className="border-t border-stone/10 hover:bg-[#F5F5F5]/50">
            <td className="py-3 px-4">
              <Link href={`/super_admin/pending_tenants/${r.id}`} className="font-semibold text-text-primary hover:underline">{r.proposed_org_name}</Link>
            </td>
            <td className="py-3 px-4 text-text-secondary">{r.contact_full_name}</td>
            <td className="py-3 px-4 text-text-secondary">{r.contact_email}</td>
            <td className="py-3 px-4 text-text-muted text-xs">{reviewed ? r.reviewed_at_display : r.requested_at_display}</td>
            <td className="py-3 px-4">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.status === 'approved' ? 'bg-[#15532E]/10 text-[#15532E]' : r.status === 'rejected' ? 'bg-[#ED1C24]/10 text-[#ED1C24]' : 'bg-[#F0AD4E]/10 text-[#8a5a00]'}`}>
                {r.status}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
